import type { UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  mobilityStatus,
  requiredAssistancesValues,
  symptomsValues,
} from "./lib";

const elderCareSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    age: z
      .string()
      .min(1, "Age is required")
      .refine((val) => /^\d.*[a-zA-Z]$/.test(val), {
        message: "Age must be a number and include a unit (e.g., 50 yrs)",
      }),
    weight: z
      .string()
      .min(1, "Weight is required")

      .transform((val) => {
        if (!val.trim().toLowerCase().endsWith("kg")) {
          return val + " kg";
        }
        return val;
      })
      .refine((val) => /^\d.*[a-zA-Z]$/.test(val), {
        message: "Weight must be a number and include a unit (e.g., 50 kg)",
      }),
    mobility_status: z.string().min(1, "Mobility Status is required"),
    symptomps: z
      .array(
        z.object({
          value: z.string(),
          selected: z.boolean(),
        }),
      )
      .default([]),
    required_assistances: z
      .array(
        z.object({
          value: z.string(),
          selected: z.boolean(),
        }),
      )
      .min(1, "Required Assistances is required"),
    additional_notes: z.string().min(1, "Additional Notes is required"),
  })
  .superRefine((val, ctx) => {
    const weightAbove50 = parseInt(val.weight) > 50;
    const isMobilityHasBedriddenOrWheels = mobilityStatus
      .slice(2, 4)
      .some((option) => option === val.mobility_status);

    if (weightAbove50 && isMobilityHasBedriddenOrWheels) {
      ctx.addIssue({
        path: ["mobility_status"],
        code: z.ZodIssueCode.custom,
        message: "Please contact our sales team for more information",
      });
    }
  });

export type ElderCareFormData = z.input<typeof elderCareSchema>;

export interface ElderCareFormReturn extends UseFormReturn<ElderCareFormData> {
  defaultValues: ElderCareFormData;
}

export const initialFormValues: ElderCareFormData = {
  name: "",
  age: "",
  weight: "",
  mobility_status: "",
  symptomps: [...symptomsValues],
  required_assistances: [...requiredAssistancesValues],
  additional_notes: "",
};

export const useElderCareForm = (
  initialValues?: ElderCareFormData,
): ElderCareFormReturn => {
  const defaultValues = initialValues || initialFormValues;

  const form = useForm<ElderCareFormData>({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(elderCareSchema),
  });

  return {
    ...form,
    defaultValues,
  };
};
